import type { PasswordResetRequestFailedAction } from '@Application/Security/PasswordResets/Actions/passwordResetRequestFailed';
import RequestPasswordReset from '@Application/Security/PasswordResets/Commands/RequestPasswordReset';
import requestPasswordReset from '@Application/Security/PasswordResets/requestPasswordReset';
import PasswordForgottenFormData from '@UI/Pages/Authentication/PasswordForgotten/FormData/PasswordForgottenFormData';
import { css } from '@emotion/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch } from '@verclam/synapse';
import { Button, Card, Form, InputText, Message } from '@verclam/ui-kit';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';

const backLinkStyle = css`
	margin-top: 1.25rem;
`;

/** Sole entry point requesting a reset link: no variant of this screen ever confirms whether the address exists. */
const PasswordForgottenPage: React.FC = () => {
	const dispatch = useDispatch();

	const [formData, setFormData] = React.useState(() => PasswordForgottenFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [hasSucceeded, setHasSucceeded] = React.useState(false);
	const [serverErrorMessage, setServerErrorMessage] = React.useState<string | null>(null);

	const handleEmailChange = React.useCallback((value: string) => {
		setFormData((current) => current.setEmail(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		setServerErrorMessage(null);
		setIsSubmitting(true);

		dispatch(requestPasswordReset(new RequestPasswordReset(formData.email)))
			.then(() => setHasSucceeded(true))
			.catch((action: PasswordResetRequestFailedAction) => {
				const fieldErrors = action.payload?.fieldErrors ?? [];

				updateValidationFromExternalSource({
					action: {
						type: action.type,
						payload: { fieldErrors },
					},
					eventNames: 'PASSWORD_RESET_LINK_REQUEST_FAILED',
					formDataUpdater: setFormData,
				});

				if (fieldErrors.length === 0) {
					setServerErrorMessage(action.message);
				}
			})
			.finally(() => setIsSubmitting(false));
	}, [dispatch, formData]);

	return (
		<Card title="Mot de passe oublié" width="100%" maxWidth="26rem">
			{hasSucceeded ? (
				<Message
					severity="success"
					text={
						'Si un compte existe pour cette adresse, un lien de réinitialisation vient d’être envoyé.'
					}
				/>
			) : (
				<Form onSubmit={handleSubmit} aria-label="Demande de réinitialisation du mot de passe">
					{serverErrorMessage !== null && (
						<Message severity="error" text={serverErrorMessage} css={fieldMarginStyles} />
					)}
					<InputText
						name="email"
						label="Adresse e-mail"
						type="email"
						autoComplete="username"
						autoFocus
						required
						value={formData.email}
						onChange={handleEmailChange}
						errorMessage={formData.getValidationMessage('email')}
						isFormFieldValid={formData.isValid('email')}
					/>
					<Button
						type="submit"
						label="Envoyer le lien"
						loading={isSubmitting}
						css={fieldMarginStyles}
					/>
				</Form>
			)}
			<p css={backLinkStyle}>
				<Link to="/login">Retour à la connexion</Link>
			</p>
		</Card>
	);
};

export default PasswordForgottenPage;
